import { useState } from 'react';
import { Body, Pill, Row, type Tone } from '@leanin/ui';
import { Tooltip } from '@leanin/ui/client';

import { RouterLinkAs } from '../../lib/router-link';

// A member's circle/network affiliations as pills, collapsed to a `+N`
// overflow chip. Names are long and columns are narrow, so the visible ones
// truncate on a word boundary and carry their full detail in a tooltip; the
// overflow chip tooltips the complete list.
const CHIP_NAME_MAX = 16;

export type GroupChipItem = {
  /** Stable react key — an id, since two circles can share a name. */
  readonly key: string;
  readonly label: string;
  readonly tone: Tone;
  /** Full detail shown on hover; falls back to `label`. */
  readonly tooltip?: string;
  /** When set, the pill becomes a link to the circle/network it names. */
  readonly href?: string;
};

function truncateChipName(name: string): string {
  if (name.length <= CHIP_NAME_MAX) return name;
  const cut = name.slice(0, CHIP_NAME_MAX - 1);
  const lastSpace = cut.lastIndexOf(' ');
  const head =
    lastSpace > CHIP_NAME_MAX - 8 ? cut.slice(0, lastSpace).trimEnd() : cut.trimEnd();
  return `${head}…`;
}

export function GroupChips({
  items,
  show = 1,
  maxWidth,
  moreLabel,
}: {
  readonly items: ReadonlyArray<GroupChipItem>;
  /** How many affiliations render as full pills before collapsing into `+N`. */
  readonly show?: number;
  /** Caps the cell's intrinsic width so pills wrap onto a second line instead
   *  of widening the table — `min-width: max-content` on DataTable's table
   *  means an unconstrained pill row stretches the whole column. */
  readonly maxWidth?: number;
  /** Accessible name for the overflow chip, e.g. "Show 2 more" — the visible
   *  `+2` is too terse to announce on its own. */
  readonly moreLabel?: (count: number) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) {
    return (
      <Body size="sm" color="mute">
        —
      </Body>
    );
  }

  const overflow = items.slice(show);
  const visible = expanded ? items : items.slice(0, show);
  const hiddenLabels = overflow.map((i) => i.tooltip ?? i.label).join(', ');

  return (
    <Row gap="xs" wrap {...(maxWidth === undefined ? {} : { style: { maxWidth } })}>
      {visible.map((item) => (
        <Tooltip key={item.key} content={item.tooltip ?? item.label} side="top" align="start">
          <Pill
            tone={item.tone}
            {...(item.href === undefined
              ? {}
              : { href: item.href, linkAs: RouterLinkAs })}
          >
            {truncateChipName(item.label)}
          </Pill>
        </Tooltip>
      ))}
      {overflow.length > 0 && !expanded && (
        // Reveals the rest in place, then gets out of the way — the row shows
        // everything and needs no re-collapse. Neutral tone because the hidden
        // set can mix leadership with plain membership, so borrowing an item's
        // tone would claim a standing that may not hold.
        <Tooltip content={hiddenLabels} side="top" align="start">
          <Pill
            tone="neutral"
            onClick={() => setExpanded(true)}
            aria-label={moreLabel?.(overflow.length)}
          >
            +{overflow.length}
          </Pill>
        </Tooltip>
      )}
    </Row>
  );
}
